import { Injectable } from '@angular/core';
import { Router, NavigationEnd, NavigationStart } from '@angular/router';
import { FlowInstance } from '@workflow/class/flow.service';
import { filter } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class RouteHistoryService {
  private previousUrl: string | null = null;
  private currentUrl: string | null = null;
  public nextUrl: string | null = null;

  constructor(private router: Router) {
    // Set initial URLs from the current active route on service construction
    this.currentUrl = this.router.url;
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      // Store the previous URL
      this.previousUrl = this.currentUrl;
      this.currentUrl = event.urlAfterRedirects;
      this.nextUrl = null;
    });
    this.router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe((event: NavigationStart) => {
      // Store the next URL being navigated to
      this.nextUrl = event.url;
    });
  }

  /**
   * Returns the previous URL visited in the router, or null if not available.
   */
  getPreviousUrl(): string | null {
    return this.previousUrl;
  }

  /**
   * Returns the current URL from the router, or null if not set.
   */
  getCurrentUrl(): string | null {
    return this.currentUrl;
  }

  /**
   * Returns the next URL being navigated to, or null if not set.
   */
  getNextUrl(): string | null {
    return this.nextUrl;
  }

  /**
   * Returns the page instance from the workflow flow by matching the given URL.
   * Returns null if the URL is not a valid workflow page or no instance is found.
   * @param url The route URL to look up
   * @param flow The workflow flow object containing pages
   */
  getPageInstanceByUrl(url: string, flow: FlowInstance): unknown | null {
    if (!url || !flow || !Array.isArray(flow.config?.pages)) {
      return null;
    }
    // Remove fragment/hash and query string from url
    const cleanUrl = url.split('#')[0].split('?')[0];
    // Extract page id from url (assuming last segment is the page id, adjust as needed)
    const segments = cleanUrl.split('/').filter(Boolean);
    const pageId = segments[segments.length - 1];
    if (!pageId) {
      return null;
    }
    // Find the page instance by id
    const page = flow.pages.find((p) => p.instanceId === pageId);
    if (!page) {
      return null;
    }
    return page;
  }

  /**
   * Determines if navigation is in the forward direction within the workflow.
   * Compares the previous and current URLs to see if the user is moving forward in the workflow page order.
   * - Returns true if the current page index is greater than the previous page index.
   * - Returns false if any URL or flow is missing, or if the page IDs are not found in the workflow.
   *
   * @param flow The workflow flow instance (must have a config.pages array and pages array)
   * @param currUrl The current route URL (e.g., from snapshot)
   * @returns True if navigation is forward, false otherwise
   */
  isForwardNavigation(flow: FlowInstance, currUrl: string): boolean {
    const prevUrl = this.getCurrentUrl();
    if (!prevUrl || !currUrl || !flow || !Array.isArray(flow.config?.pages)) {
      return false;
    }
    // Remove fragments and query params from both URLs
    const cleanPrevUrl = prevUrl.split('#')[0].split('?')[0];
    const cleanCurrUrl = currUrl.split('#')[0].split('?')[0];
    // Extract page ids from the last segment of each URL
    const prevSegments = cleanPrevUrl.split('/').filter(Boolean);
    const currSegments = cleanCurrUrl.split('/').filter(Boolean);
    const prevPageId = prevSegments[prevSegments.length - 1];
    const currPageId = currSegments[currSegments.length - 1];
    if (!prevPageId || !currPageId) {
      return false;
    }
    // Find indices in the workflow page order (using path or config.id)
    const pageOrder = flow.pages.map((t) => t.path || t.config.id);
    const prevIndex = pageOrder.indexOf(prevPageId);
    const currIndex = pageOrder.indexOf(currPageId);
    if (prevIndex === -1 || currIndex === -1) {
      return false;
    }
    // Navigation is forward if the current page index is greater than the previous
    return currIndex > prevIndex;
  }
}
